<template>
  <div class="menu_switcher--container"
       :class="containerClasses"
       :style="containerStyles"
       v-if="visibleMenus.length">

    <a class="menu_switcher--handle visual_menu--glass_effect textual_menu--glass_effect"
       v-if="isCollapsableOrMobileDockLeft"
       href="#" :title="'Click to ' + ((isOpen) ? 'close' : 'open') + ' available menus for ' + menuPageTitle"
       @click.stop.prevent="isOpen = !isOpen">
      <span class="material-icons">menu_book</span>
      <span v-show="!isOpen" class="text">Available Menus</span>
      <span v-show="isOpen" class="text">Close</span>
    </a>

    <div class="viewport visual_menu--glass_effect textual_menu--glass_effect">
      <template v-for="menu in visibleMenus">
        <a class="menu_switcher--menu" href="#"
           :class="{'menu_switcher--menu-active': currentMenuId === menu.menu_id}"
           @click.prevent="switchMenus(menu.menu_id)"
           :title="'Click to see the ' + getMenuTitle(menu) + ' menu'">
          <span v-if="menu.menu_icon.length" class="menu_switcher--icon material-icons" aria-hidden="true">{{ menu.menu_icon }}</span>
          <span class="menu_switcher--title" v-show="isDockLeft || 1 > percentScroll">{{
              menu.menu_type.mt_title
            }}</span>
        </a>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Visual menu switcher component.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.5
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2022-05-24 (1.0.0) (jhoward)
 *    - <menu-switcher> facilitates quickly changing menus within the set of menus passed in as "menus" prop.
 *
 *  2022-10-05 (1.0.1) (jhoward)
 *    - Fix bug in layout of <menu-switcher> where the closed viewport would still overlay much of the page.
 *
 *  2023-03-24 (1.1.0) (jhoward)
 *    - Add omnipresent version of <menu-switcher> with menu icons.
 *
 *  2023-06-19 (1.1.1) (jhoward)
 *    - Make background partially see-through in <menu-switcher>.
 *
 *  2023-07-26 (1.1.2) (jhoward)
 *    - Add custom styles in <menu-switcher> for CHI menus.
 *
 *  2024-07-09 (1.2.0) (jhoward)
 *    - Add isDockLeft prop in <menu-switcher>.
 *    - Add accessibility-related attributes in <menu-switcher>.
 *
 *  2024-07-10 (1.2.1) (jhoward)
 *    - Modify menu button styles in <menu-switcher>.
 *    - Improve menu button placement in <menu-switcher>.
 *
 *  2024-07-19 (1.2.2) (jhoward)
 *    - Do not render <menu-switcher> if no related menus exist.
 *
 *  2024-12-03 (1.2.3) (jhoward)
 *    - Fix <menu-switcher> displaying over content when closed.
 *
 *  2024-12-03 (1.2.4) (jhoward)
 *    - Change <menu-switcher> layout, giving each menu button more space and breaking on words if words are too long.
 *
 *  2025-01-24 (1.2.5) (jhoward)
 *    - Hide menu icons from screen readers in <menu-switcher>.
 */

import {defineComponent, PropType} from "vue";
import {Menu, VisualMenuComponent, TextualMenuComponent, MenuSwitcherComponent} from "./defs";
import {methodIsModelScheduled} from "../../helpers/menu-sections";

export default defineComponent({
  name: 'menu-switcher',
  emits: {
    opened: () => true,
    closed: () => true,
  },
  props: {
    menus: {
      type: Array as PropType<Menu[]>,
      required: true
    },
    currentMenuId: {
      type: Number,
      default: 0
    },
    deviceAssetCode: {
      type: String,
      default: ''
    },
    isCollapsable: {
      type: Boolean,
      default: false
    },
    isDockLeft: {
      type: Boolean,
      default: false
    }
  },
  mounted: function () {
    document.addEventListener('click', () => {
      if (this.isCollapsableOrMobileDockLeft) {
        this.isOpen = false;
      }
    });
    if (!this.isDockLeft) {
      document.addEventListener('scroll', () => {
        this.windowScrollY = window.scrollY;
      }, {passive: true});
    }

    window.addEventListener('resize', () => {
      window.clearTimeout(this.windowWidthTimer);
      this.windowWidthTimer = window.setTimeout(() => {
        this.windowWidth = window.innerWidth;

        if (this.isDockLeft) {
          this.setDockBottom();
        }
      }, 128);
    }, {passive: true});

    window.setTimeout(this.setDockBottom, 1024);
  },
  data: function () {
    return {
      isOpen: false,
      maxHeight: 128,
      minHeight: 72,
      maxFontSize: 16,
      minFontSize: 0,
      maxVerticalPadding: 16,
      minVerticalPadding: 8,
      switcherHeight: 128,

      percentScroll: 0,
      windowScrollY: 0,

      windowWidthTimer: -1,
      windowWidth: window.innerWidth,

      menuSectionContainer: null as HTMLDivElement | null,
      dockLeftBottom:  80,
    };
  },
  watch: {
    isOpen() {
      if (this.isOpen) {
        this.$emit('opened');
      } else {
        this.$emit('closed');
      }
    }
  },
  computed: {
    containerStyles(): { [key: string]: number | string } {
      this.switcherHeight = Math.min(this.maxHeight, Math.max(this.minHeight, this.maxHeight - this.windowScrollY));
      this.percentScroll = Math.min(1, Math.max(0, this.windowScrollY / (this.maxHeight - this.minHeight)));
      return {
        '--menu-count': this.visibleMenus.length,
        '--switcher-height': this.switcherHeight + 'px',
        '--switcher-font-size': this.minFontSize + ((this.maxFontSize - this.minFontSize) * (1 - this.percentScroll)) + 'px',
        '--switcher-vertical-padding': this.minVerticalPadding + ((this.maxVerticalPadding - this.minVerticalPadding) * (1 - this.percentScroll)) + 'px',
        '--switcher-dock-left-bottom': this.dockLeftBottom + 'px',
      };
    },
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu_switcher--container-open': this.isOpen,
        'menu_switcher--container-collapsable': this.isCollapsableOrMobileDockLeft,
        'menu_switcher--container-dock_left': this.isDockLeft,
      };
    },

    isMobileUi(): boolean {
      return this.windowWidth < 800;
    },

    isCollapsableOrMobileDockLeft(): boolean {
      return this.isCollapsable || (this.isDockLeft && this.isMobileUi);
    },

    menuPageTitle(): string {
      return this.visibleMenus[0]?.page?.page_h1 || '';
    },

    visibleMenus(): Menu[] {
      return Array.from(this.menus.filter((menu) => {
        return (this as MenuSwitcherComponent).isModelScheduled(menu.normalizedSchedules || []);
      }));
    },
  },
  methods: {
    setDockBottom() {
      if (!this.menuSectionContainer) {
        this.menuSectionContainer = document.querySelector('.body_container--section_nav') as HTMLDivElement | null;
      }

      if (this.menuSectionContainer && this.isDockLeft) {
        this.dockLeftBottom = this.menuSectionContainer.getBoundingClientRect().height;
      }
    },
    getMenuTitle(menu: Menu): string {
      return menu.menu_type?.mt_title || menu.full_title || 'Menu';
    },

    switchMenus(menuId: number) {
      (this.$parent as VisualMenuComponent | TextualMenuComponent).switchToRelatedMenu(menuId);
    },

    isModelScheduled: methodIsModelScheduled,
  }
})
</script>

<style scoped>
.menu_switcher--container {
  --menu-count: 1;
  --switcher-height: 128px;
  --switcher-font-size: 16px;
  --switcher-vertical-padding: 16px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 20;
  font-size: 0.7em;
}

.menu_switcher--container-dock_left {
  --switcher-dock-left-bottom: 70px;
  /*bottom: var(--switcher-dock-left-bottom);*/
  max-height: calc(100vh - var(--switcher-dock-left-bottom));
  width: 192px;
  z-index: 30;
  overflow-y: auto;
}

.menu_switcher--container-collapsable.menu_switcher--container-dock_left {
  width: 6em;
}

.menu_switcher--container-collapsable.menu_switcher--container-dock_left.menu_switcher--container-open {
  width: 192px;
}


@media screen and (max-width: 800px) {
/*  .menu_switcher--container.menu_switcher--container-dock_left {
    width: 192px;
  }*/

  .menu_switcher--handle .text {
    display: none;
  }
}

.menu_switcher--container-collapsable:not(.menu_switcher--container-dock_left) {
  z-index: 5;
}

.menu_switcher--container-collapsable.menu_switcher--container-open {
  z-index: 20;
}

.menu_switcher--container h3 {
  display: inline-block;
}

.viewport {
  display: grid;
  grid-gap: 1em;
  grid-template-columns: repeat(var(--menu-count), minmax(10em, 1fr));
  overflow-x: auto;
  overflow-y: hidden;
  padding-top: var(--switcher-vertical-padding);
  padding-bottom: var(--switcher-vertical-padding);
}

.menu_switcher--container-dock_left .viewport {
  grid-template-columns: 1fr;
  overflow: initial;
  min-height: 100%;
  grid-gap: 1rem;
  padding: 1rem;
}

.menu_switcher--container-collapsable .viewport {
  padding-top: 5.2em;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 1ms linear 150ms, height 150ms linear;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-100%);
  height: 0;
  overflow: hidden;
}

.menu_switcher--container-collapsable.menu_switcher--container-open .viewport {
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  visibility: visible;
  transform: none;
  height: auto;
  overflow: scroll;
}

.menu_switcher--container-collapsable .menu_switcher--handle {
  position: absolute;
  top: 0;
  left: 0.25em;
  text-decoration: none;
  z-index: 10;
  padding: 0.25em 0.5em 0.25em 0.25em;
  border-radius: 0.25em;
  font-size: 1.2em;
}

.menu_switcher--handle .material-icons {
  font-size: 3em;
  vertical-align: middle;
}

.menu_switcher--menu {
  border-radius: 0.25em;
  box-shadow: 1px 1px 6px 0 rgba(0,0,0,.35);
  display: block;
  text-align: center;
  grid-template-rows: 1fr 0.2fr;
  text-decoration: none;
  box-sizing: border-box;
  color: #007966;
  padding: 1em;
  background-color: #FFF;
  will-change: color, background-color;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu_switcher--container-dock_left .menu_switcher--menu {
  height: initial;
  align-self: center;
}

.menu_switcher--menu-active {
  background-color: #007966;
  color: #FFF;
}

.menu_switcher--menu:hover {
  color: #007966;
  background-color: rgba(0, 121, 102, 0.13);
}

.menu_switcher--menu-active:hover {
  color: #FFF;
  background-color: rgba(0, 121, 102, 0.87);
}

.visual_menu--container-chi .menu_switcher--menu-active {
  background-color: rgb(221, 174, 91);
}

.menu_switcher--menu span {
  display: block;
}

.menu_switcher--icon {
  font-size: calc(var(--switcher-font-size) * 0.75 + 1.5em);
}

.menu_switcher--title {
  font-size: var(--switcher-font-size);
  text-transform: uppercase;
  word-break: break-word;
}

.menu_switcher--icon + .menu_switcher--title {
  margin-top: var(--switcher-font-size);
}

.menu_switcher--container-dock_left .menu_switcher--title {
  font-size: 1.3em;
}
</style>